import { useState, useRef } from 'react'
import {
  Play,
  Save,
  Clock,
  Database,
  Table2,
  ChevronRight,
  ChevronDown,
  Copy,
  Download,
  Plus,
  X,
  Sparkles,
  Loader2,
  CheckCircle,
  AlertCircle,
  FileCode,
  History,
  Bookmark,
} from 'lucide-react'

interface Tab {
  id: string
  name: string
  query: string
  results: any[] | null
  status: 'idle' | 'running' | 'success' | 'error'
  executionTime?: number
}

interface TableSchema {
  name: string
  columns: { name: string; type: string }[]
}

const sampleTables: TableSchema[] = [
  {
    name: 'sales',
    columns: [
      { name: 'id', type: 'integer' },
      { name: 'date', type: 'timestamp' },
      { name: 'region', type: 'varchar' },
      { name: 'product', type: 'varchar' },
      { name: 'amount', type: 'decimal' },
      { name: 'quantity', type: 'integer' },
    ],
  },
  {
    name: 'customers',
    columns: [
      { name: 'id', type: 'integer' },
      { name: 'name', type: 'varchar' },
      { name: 'email', type: 'varchar' },
      { name: 'created_at', type: 'timestamp' },
    ],
  },
  {
    name: 'products',
    columns: [
      { name: 'id', type: 'integer' },
      { name: 'name', type: 'varchar' },
      { name: 'category', type: 'varchar' },
      { name: 'price', type: 'decimal' },
    ],
  },
]

const sampleResults = [
  { region: 'North', total_sales: 125000, order_count: 342 },
  { region: 'South', total_sales: 98000, order_count: 287 },
  { region: 'East', total_sales: 156000, order_count: 423 },
  { region: 'West', total_sales: 134000, order_count: 398 },
  { region: 'Central', total_sales: 87000, order_count: 234 },
]

export function SQLLab() {
  const [tabs, setTabs] = useState<Tab[]>([
    {
      id: '1',
      name: 'Query 1',
      query: `SELECT
  region,
  SUM(amount) as total_sales,
  COUNT(*) as order_count
FROM sales
WHERE date >= '2024-01-01'
GROUP BY region
ORDER BY total_sales DESC;`,
      results: null,
      status: 'idle',
    },
  ])
  const [activeTabId, setActiveTabId] = useState('1')
  const [expandedTables, setExpandedTables] = useState<string[]>(['sales'])
  const [selectedConnection, setSelectedConnection] = useState('Production DB')
  const [showAiAssistant, setShowAiAssistant] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [isAiGenerating, setIsAiGenerating] = useState(false)

  const activeTab = tabs.find((t) => t.id === activeTabId)!

  const runQuery = () => {
    setTabs(
      tabs.map((t) =>
        t.id === activeTabId ? { ...t, status: 'running' as const } : t
      )
    )

    setTimeout(() => {
      setTabs(
        tabs.map((t) =>
          t.id === activeTabId
            ? {
                ...t,
                status: 'success' as const,
                results: sampleResults,
                executionTime: 0.234,
              }
            : t
        )
      )
    }, 1500)
  }

  const addTab = () => {
    const newTab: Tab = {
      id: Date.now().toString(),
      name: `Query ${tabs.length + 1}`,
      query: '',
      results: null,
      status: 'idle',
    }
    setTabs([...tabs, newTab])
    setActiveTabId(newTab.id)
  }

  const closeTab = (tabId: string) => {
    if (tabs.length === 1) return
    const newTabs = tabs.filter((t) => t.id !== tabId)
    setTabs(newTabs)
    if (activeTabId === tabId) {
      setActiveTabId(newTabs[0].id)
    }
  }

  const handleAiGenerate = () => {
    setIsAiGenerating(true)
    setTimeout(() => {
      setTabs(
        tabs.map((t) =>
          t.id === activeTabId
            ? {
                ...t,
                query: `-- Generated by Kodee AI
SELECT
  p.category,
  DATE_TRUNC('month', s.date) as month,
  SUM(s.amount) as revenue,
  COUNT(DISTINCT c.id) as unique_customers
FROM sales s
JOIN products p ON s.product_id = p.id
JOIN customers c ON s.customer_id = c.id
WHERE s.date >= DATE_TRUNC('year', CURRENT_DATE)
GROUP BY p.category, DATE_TRUNC('month', s.date)
ORDER BY month, revenue DESC;`,
              }
            : t
        )
      )
      setIsAiGenerating(false)
      setShowAiAssistant(false)
      setAiPrompt('')
    }, 2000)
  }

  const toggleTable = (tableName: string) => {
    setExpandedTables((prev) =>
      prev.includes(tableName)
        ? prev.filter((t) => t !== tableName)
        : [...prev, tableName]
    )
  }

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <FileCode className="w-5 h-5 text-primary-400" />
            <span className="text-lg font-semibold text-white">SQL Lab</span>
          </div>
          <select
            value={selectedConnection}
            onChange={(e) => setSelectedConnection(e.target.value)}
            className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white"
          >
            <option>Production DB</option>
            <option>Analytics Warehouse</option>
            <option>Staging DB</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAiAssistant(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <Sparkles className="w-4 h-4" />
            Kodee AI
          </button>
          <button className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:bg-gray-700 rounded-lg">
            <History className="w-4 h-4" />
            History
          </button>
          <button className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:bg-gray-700 rounded-lg">
            <Bookmark className="w-4 h-4" />
            Saved
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Schema Browser */}
        <div className="w-64 bg-gray-800 border-r border-gray-700 flex flex-col">
          <div className="p-3 border-b border-gray-700">
            <div className="relative">
              <input
                type="text"
                placeholder="Search tables..."
                className="w-full px-3 py-2 pl-9 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400"
              />
              <Database className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          <div className="flex-1 overflow-auto p-2">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider px-2 py-2">
              Tables
            </div>
            {sampleTables.map((table) => (
              <div key={table.name} className="mb-1">
                <button
                  onClick={() => toggleTable(table.name)}
                  className="flex items-center gap-2 w-full px-2 py-2 text-left text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {expandedTables.includes(table.name) ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                  <Table2 className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">{table.name}</span>
                  <span className="ml-auto text-xs text-gray-500">
                    {table.columns.length}
                  </span>
                </button>

                {expandedTables.includes(table.name) && (
                  <div className="ml-6 mt-1 space-y-0.5">
                    {table.columns.map((col) => (
                      <div
                        key={col.name}
                        className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-400 hover:bg-gray-700/50 rounded cursor-pointer group"
                        onClick={() => {
                          setTabs(
                            tabs.map((t) =>
                              t.id === activeTabId
                                ? {
                                    ...t,
                                    query: t.query + `${table.name}.${col.name}`,
                                  }
                                : t
                            )
                          )
                        }}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${
                            col.type.includes('int') || col.type === 'decimal'
                              ? 'bg-green-400'
                              : col.type.includes('time')
                              ? 'bg-yellow-400'
                              : 'bg-blue-400'
                          }`}
                        />
                        <span className="flex-1">{col.name}</span>
                        <span className="text-xs text-gray-500 group-hover:text-gray-400">
                          {col.type}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Tabs */}
          <div className="flex items-center bg-gray-800 border-b border-gray-700 px-2">
            {tabs.map((tab) => (
              <div
                key={tab.id}
                className={`flex items-center gap-2 px-4 py-2 cursor-pointer border-b-2 transition-colors ${
                  activeTabId === tab.id
                    ? 'border-primary-500 text-white bg-gray-700/50'
                    : 'border-transparent text-gray-400 hover:text-gray-200'
                }`}
                onClick={() => setActiveTabId(tab.id)}
              >
                <span className="text-sm">{tab.name}</span>
                {tab.status === 'running' && (
                  <Loader2 className="w-3 h-3 animate-spin text-primary-400" />
                )}
                {tab.status === 'success' && (
                  <CheckCircle className="w-3 h-3 text-green-400" />
                )}
                {tab.status === 'error' && (
                  <AlertCircle className="w-3 h-3 text-red-400" />
                )}
                {tabs.length > 1 && (
                  <X
                    className="w-3 h-3 hover:text-red-400"
                    onClick={(e) => {
                      e.stopPropagation()
                      closeTab(tab.id)
                    }}
                  />
                )}
              </div>
            ))}
            <button
              onClick={addTab}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg ml-1"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Editor */}
          <div className="flex-1 flex flex-col">
            <div className="flex-1 relative">
              <textarea
                value={activeTab.query}
                onChange={(e) =>
                  setTabs(
                    tabs.map((t) =>
                      t.id === activeTabId ? { ...t, query: e.target.value } : t
                    )
                  )
                }
                placeholder="-- Write your SQL query here..."
                className="w-full h-full p-4 bg-gray-900 text-gray-100 font-mono text-sm resize-none focus:outline-none"
                spellCheck={false}
              />

              {/* Run Button */}
              <div className="absolute bottom-4 right-4 flex items-center gap-2">
                <span className="text-xs text-gray-500">⌘ + Enter to run</span>
                <button
                  onClick={runQuery}
                  disabled={activeTab.status === 'running'}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors"
                >
                  {activeTab.status === 'running' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Run Query
                </button>
              </div>
            </div>

            {/* Results Panel */}
            <div className="h-64 bg-gray-800 border-t border-gray-700">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-white">Results</span>
                  {activeTab.results && (
                    <span className="text-xs text-gray-400">
                      {activeTab.results.length} rows in {activeTab.executionTime}s
                    </span>
                  )}
                </div>
                {activeTab.results && (
                  <div className="flex items-center gap-2">
                    <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded">
                      <Copy className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="overflow-auto h-48">
                {activeTab.status === 'running' && (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                  </div>
                )}

                {activeTab.results && (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-700/50 sticky top-0">
                      <tr>
                        {Object.keys(activeTab.results[0]).map((key) => (
                          <th
                            key={key}
                            className="px-4 py-2 text-left text-gray-300 font-medium"
                          >
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeTab.results.map((row, i) => (
                        <tr
                          key={i}
                          className="border-b border-gray-700 hover:bg-gray-700/30"
                        >
                          {Object.values(row).map((val: any, j) => (
                            <td key={j} className="px-4 py-2 text-gray-300">
                              {typeof val === 'number'
                                ? val.toLocaleString()
                                : val}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {!activeTab.results && activeTab.status === 'idle' && (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <Database className="w-8 h-8 mb-2" />
                    <span className="text-sm">Run a query to see results</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Assistant Modal */}
      {showAiAssistant && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    Kodee AI SQL Assistant
                  </h2>
                  <p className="text-sm text-gray-400">
                    Describe what you want to query in plain English
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g., Show me monthly revenue by product category for this year, including the number of unique customers..."
                className="w-full h-32 p-4 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
              />

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    setAiPrompt('Top 10 customers by total purchase amount')
                  }
                  className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-full text-xs hover:bg-gray-600"
                >
                  Top customers
                </button>
                <button
                  onClick={() =>
                    setAiPrompt('Monthly sales trend for the last 12 months')
                  }
                  className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-full text-xs hover:bg-gray-600"
                >
                  Sales trend
                </button>
                <button
                  onClick={() =>
                    setAiPrompt('Products with declining sales this quarter')
                  }
                  className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-full text-xs hover:bg-gray-600"
                >
                  Declining products
                </button>
              </div>
            </div>

            <div className="p-6 bg-gray-700/30 flex justify-end gap-3">
              <button
                onClick={() => setShowAiAssistant(false)}
                className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAiGenerate}
                disabled={!aiPrompt.trim() || isAiGenerating}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {isAiGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate SQL
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
